import { useState } from "react";
import { Mail, Phone, MapPin, Send, MessageSquare } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { useToast } from "../hooks/use-toast";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SparkleButton from "../components/SparkleButton";
import { agencyInfo } from "../mock";

const ContactPage = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Mock form submission
    setTimeout(() => {
      toast({
        title: "Message Sent!",
        description: "We'll get back to you within 24 hours.",
      });
      setFormData({
        name: "",
        email: "",
        phone: "",
        service: "",
        message: ""
      });
      setIsSubmitting(false);
    }, 1500);
  };

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-black text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            backgroundSize: '60px 60px'
          }}></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className="inline-block animate-fade-in">
              <Badge className="px-4 py-2 bg-gradient-to-r from-orange-500/20 to-yellow-400/20 border border-orange-400/40 text-orange-300 text-sm font-bold">
                💬 Get In Touch 💬
              </Badge>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold animate-slide-in-left">
              Let's Work <span className="bg-gradient-to-r from-orange-500 via-yellow-400 to-orange-500 bg-clip-text text-transparent">Together</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed animate-slide-in-right">
              Have a project in mind? I'd love to hear from you. Send me a message and I'll respond as soon as possible.
            </p>
            <div className="golden-accent-line"></div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="border-2 hover:border-blue-400 hover-lift animate-fade-in">
                <CardContent className="p-6 space-y-6">
                  <div className="space-y-2">
                    <h3 className="text-2xl font-bold text-gray-900">Contact Information</h3>
                    <p className="text-gray-600">Reach out through any of these channels</p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Phone className="text-blue-600" size={24} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Phone</h4>
                        <a
                          href={`tel:${agencyInfo.whatsapp[0]}`}
                          className="text-sm text-gray-600 hover:text-blue-600 transition-colors duration-300 block"
                        >
                          {agencyInfo.whatsapp[0]}
                        </a>
                        <a
                          href={`tel:${agencyInfo.whatsapp[1]}`}
                          className="text-sm text-gray-600 hover:text-blue-600 transition-colors duration-300 block"
                        >
                          {agencyInfo.whatsapp[1]}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Mail className="text-orange-600" size={24} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                        <a
                          href={`mailto:${agencyInfo.email}`}
                          className="text-sm text-gray-600 hover:text-orange-600 transition-colors duration-300"
                        >
                          {agencyInfo.email}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <MapPin className="text-green-600" size={24} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Locations</h4>
                        <div className="space-y-2">
                          {agencyInfo.locations.map((location, index) => (
                            <div key={index} className="flex items-center space-x-2 text-sm text-gray-600">
                              <span className="text-lg">{location.flag}</span>
                              <span className="font-medium">{location.full}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 bg-gradient-to-br from-blue-600 to-orange-500 text-white animate-fade-in" style={{ animationDelay: '0.2s' }}>
                <CardContent className="p-6 space-y-4">
                  <MessageSquare size={40} />
                  <h3 className="text-xl font-bold">Prefer WhatsApp?</h3>
                  <p className="text-sm text-blue-100">Get instant responses through WhatsApp</p>
                  <Button
                    asChild
                    className="w-full bg-white text-blue-600 hover:bg-gray-100"
                  >
                    <a
                      href={`https://wa.me/${agencyInfo.whatsapp[0].replace(/\+/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Chat on WhatsApp
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-2 animate-slide-in-right">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-2">
                      <h3 className="text-2xl font-bold text-gray-900">Send Us a Message</h3>
                      <p className="text-gray-600">Fill out the form below and we'll get back to you soon</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="border-2 focus:border-blue-400"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="john@example.com"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="border-2 focus:border-blue-400"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="+1 234 567 8900"
                          value={formData.phone}
                          onChange={handleChange}
                          className="border-2 focus:border-blue-400"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="service">Service Interested In</Label>
                        <select
                          id="service"
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border-2 border-gray-200 rounded-md focus:border-blue-400 focus:outline-none transition-colors duration-300"
                        >
                          <option value="">Select a service</option>
                          <option value="Video Editing">Video Editing</option>
                          <option value="Content Creation">Content Creation</option>
                          <option value="Full Stack Development">Full Stack Development</option>
                          <option value="Story Telling">Story Telling</option>
                          <option value="Digital Marketing">Digital Marketing</option>
                          <option value="Ethical Hacking">Ethical Hacking</option>
                          <option value="Graphic Design">Graphic Design</option>
                        </select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Your Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        placeholder="Tell us about your project..."
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="border-2 focus:border-blue-400 resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-blue-600 to-orange-500 text-white hover:from-blue-700 hover:to-orange-600"
                    >
                      {isSubmitting ? (
                        "Sending..."
                      ) : (
                        <>
                          Send Message <Send className="ml-2" size={18} />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-black border-t border-yellow-500/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">
                My <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">Locations</span>
              </h2>
              <p className="text-gray-300 text-lg">Available worldwide, based in two strategic locations</p>
              <div className="golden-accent-line"></div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Maryland, USA */}
              <Card className="dark-card border border-yellow-500/20 hover:border-yellow-400 overflow-hidden">
                <div className="h-80 relative">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3094618.4401193643!2d-79.47805768750003!3d39.04575395!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89b64debe9f190df%3A0xf2af37657655f6b1!2sMaryland%2C%20USA!5e0!3m2!1sen!2s!4v1234567890123!5m2!1sen!2s"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="grayscale hover:grayscale-0 transition-all duration-500"
                  ></iframe>
                </div>
                <CardContent className="p-6 space-y-4 bg-gradient-to-br from-gray-900 to-black">
                  <div className="flex items-center space-x-3">
                    <span className="text-5xl">🇺🇸</span>
                    <div>
                      <h3 className="text-2xl font-bold text-white">Maryland</h3>
                      <p className="text-gray-300">United States</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 pt-4 border-t border-gray-700">
                    <MapPin className="text-yellow-400 mt-1 flex-shrink-0" size={20} />
                    <p className="text-gray-300 text-sm">{agencyInfo.locations[0].full}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Trincomalee, Sri Lanka */}
              <Card className="dark-card border border-yellow-500/20 hover:border-yellow-400 overflow-hidden">
                <div className="h-80 relative">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d126249.82580780668!2d81.15076825!3d8.5874042!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3afac3d2e2b47f0b%3A0x2c63e344ab9a7536!2sTrincomalee%2C%20Sri%20Lanka!5e0!3m2!1sen!2s!4v1234567890123!5m2!1sen!2s"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="grayscale hover:grayscale-0 transition-all duration-500"
                  ></iframe>
                </div>
                <CardContent className="p-6 space-y-4 bg-gradient-to-br from-gray-900 to-black">
                  <div className="flex items-center space-x-3">
                    <span className="text-5xl">🇱🇰</span>
                    <div>
                      <h3 className="text-2xl font-bold text-white">Trincomalee</h3>
                      <p className="text-gray-300">Sri Lanka</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 pt-4 border-t border-gray-700">
                    <MapPin className="text-yellow-400 mt-1 flex-shrink-0" size={20} />
                    <p className="text-gray-300 text-sm">{agencyInfo.locations[1].full}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Global Reach Banner */}
            <div className="mt-12 text-center">
              <div className="inline-block bg-gradient-to-r from-yellow-400/10 to-orange-500/10 border border-yellow-400/30 rounded-2xl px-8 py-6">
                <p className="text-yellow-400 font-bold text-lg mb-2">🌍 Global Reach, Local Expertise</p>
                <p className="text-gray-300">Operating across two continents to serve you better</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;